import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';

interface FirebaseRuntimeConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

interface FirebaseServices {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  analytics: Analytics | null;
}

const FIREBASE_CONFIG_ENDPOINT = '/.netlify/functions/firebase-config';

let firebaseServicesPromise: Promise<FirebaseServices> | null = null;

const fetchFirebaseConfig = async (): Promise<FirebaseRuntimeConfig> => {
  const response = await fetch(FIREBASE_CONFIG_ENDPOINT, {
    headers: {
      Accept: 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Unable to load Firebase configuration (${response.status})`);
  }

  return response.json() as Promise<FirebaseRuntimeConfig>;
};

const createFirebaseServices = async (): Promise<FirebaseServices> => {
  const firebaseConfig = await fetchFirebaseConfig();
  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const db = getFirestore(app);

  let analytics: Analytics | null = null;

  if (firebaseConfig.measurementId && typeof window !== 'undefined' && await isSupported()) {
    analytics = getAnalytics(app);
  }

  return {
    app,
    auth,
    db,
    analytics
  };
};

export const initializeFirebase = (): Promise<FirebaseServices> => {
  if (!firebaseServicesPromise) {
    firebaseServicesPromise = createFirebaseServices();
  }

  return firebaseServicesPromise;
};

export const getFirebaseApp = async (): Promise<FirebaseApp> => (await initializeFirebase()).app;
export const getFirebaseAuth = async (): Promise<Auth> => (await initializeFirebase()).auth;
export const getFirebaseDb = async (): Promise<Firestore> => (await initializeFirebase()).db;
export const getFirebaseAnalytics = async (): Promise<Analytics | null> => (await initializeFirebase()).analytics;
